// pages/FormViewerPage/hooks/useFormViewer/submitRpc.ts — actionsSubmit.ts split (sub-module RPC submit)
import { supabase } from '../../../../supabase/client';
import { trackFormSubmit } from '../../../../utils/trackingUtils';
import { normalizePhoneForDb } from '../../../../utils/formatPhone';
import { sendOrderConfirmations } from './submitConfirmation';
import { fireCapiEvents } from './submitCapi';
import { fireSubmitFunnelEvent } from './submitFunnel';
import { storePhoneLast4 } from '../../../../hooks/useThankYouPage/fetchOrder';
import { alignEmbedThanks } from '../../services/formViewerService/embedThanksAlign';

/**
 * Eksekusi submit RPC + efek sukses (counter, tracking, cleanup cart,
 * konfirmasi WA, CAPI). Throw jika gagal — caller menangani retry/duplikat.
 */
export async function submitPublicOrder({ s, c, built, isSimpleEmbed, isSeamlessEmbed }: any) {
    const { form, identifier, navigate, hasSubmittedRef, globalTrackingSettings, sendOrderConfirmation, quantity } = s;
    const { shippingCost, codFee, currentCombination } = c;

    const { pOrder, assignedCsId, name, email, phone, productName, sellingPrice, brandId, addressText, province, city, district, village } = built;
    // Total final (sudah termasuk diskon kupon) dipakai untuk tracking/CAPI/WA — samakan
    // dengan yang disimpan server.
    const orderTotal = Number(pOrder?.totalPrice) || 0;

    // Timeout 20s — tanpa ini, koneksi mobile yang stall bikin request menggantung
    // selamanya dan tombol submit disabled permanen sampai reload (fix C1).
    const { data: orderResult, error: submitError } = await supabase
        .rpc('submit_public_order', { p_order: pOrder })
        .abortSignal(AbortSignal.timeout(20000));
    if (submitError) throw submitError;
    if (!orderResult) throw new Error('Gagal membuat pesanan');
    const orderData = Array.isArray(orderResult) ? orderResult[0] : orderResult;
    if (!orderData?.id) throw new Error('Gagal mendapatkan ID pesanan');

    hasSubmittedRef.current = true;

    // Order dari prospek CS (?lead=): tautkan order_id balik ke bot_leads agar
    // kolom Pesanan (CO) ikut hijau. Via RPC SECURITY DEFINER karena form jalan
    // cross-origin (domain form) tanpa sesi login CS. Gagal menaut tidak boleh
    // menggagalkan order.
    try {
        const linkLeadId = sessionStorage.getItem('leadOrderLink');
        if (linkLeadId && orderData?.id) {
            sessionStorage.removeItem('leadOrderLink');
            supabase.rpc('link_lead_order', { p_lead_id: linkLeadId, p_order_id: orderData.id }).then(() => {}, () => {});
        }
    } catch { /* abaikan */ }

    // Increment submission counter
    if (form?.id) { try { await supabase.rpc('increment_form_submission', { p_form_id: form.id }) } catch { /* */ } }

    const variantLabel = currentCombination?.attributes
        ? Object.values(currentCombination.attributes).join(' / ')
        : undefined;
    trackFormSubmit(
        form?.id || '', form?.title || '',
        orderData.id, form?.productId || '', productName, brandId,
        phone, email, name,
        variantLabel, quantity, orderTotal,
    );
    // Simpan 4 digit terakhir HP di localStorage — dipakai fetchOrder.ts sebagai faktor
    // kedua get_public_order_limited (fix A2), transparan untuk pemesan asli.
    storePhoneLast4(orderData.id, phone);
    const query = new URLSearchParams(window.location.search);
    if (isSimpleEmbed) query.set('embed', 'simple'); else if (isSeamlessEmbed) query.set('embed', 'seamless');
    const suffix = query.size ? `?${query}` : '';
    const dest = form?.thankYouPage?.verificationEnabled ? `/f/${identifier}/confirm/${orderData.id}${suffix}` : `/f/${identifier}/thanks/${orderData.id}${suffix}`;
    navigate(dest, { replace: true });

    // Embed: cegah kesan blank — scroll internal + minta parent scroll + resize ulang.
    // Gagal alignment tidak boleh menggagalkan order — helper sudah try/catch dalam.
    if (isSimpleEmbed || isSeamlessEmbed) {
        try { alignEmbedThanks(identifier); } catch { /* abaikan */ }
    }

    let sCartId: string | null = null;
    try { sCartId = sessionStorage.getItem(`abandonedCart_${form?.id}`); } catch { /* iframe sandbox: abaikan */ }
    if (sCartId) { try { await supabase.rpc('cleanup_abandoned_cart', { p_cart_id: sCartId, p_phone: normalizePhoneForDb(phone), p_form_id: form?.id || null }); } catch { /* */ } try { sessionStorage.removeItem(`abandonedCart_${form?.id}`); } catch { /* */ } }

    // Fire CAPI events server-side (fire-and-forget) — dipanggil SEBELUM WA agar
    // WA yang lambat/hang tidak ikut menahan pengiriman event konversi.
    fireCapiEvents({ globalTrackingSettings, form, phone, email, name, sellingPrice, shippingCost, codFee, productName, orderData, orderValue: orderTotal, customerCity: city, customerProvince: province, customerPostalCode: (orderData as any)?.postalCode || undefined });

    // Fire event funel Meta (AddToCart/InitiateCheckout) — intent nyata, bukan view. Otomatis value + AAM.
    fireSubmitFunnelEvent(form, globalTrackingSettings, orderData.id, { orderTotal, email, phone, name });

    if (orderData && form) {
        await sendOrderConfirmations({
            orderData, form, brandId, assignedCsId, phone, name, productName,
            totalPrice: orderTotal,
            addressText, province, city, district, village, sendOrderConfirmation,
        });
    }

    return orderData;
}
